import React, { useState, useCallback } from 'react';
import { createStrategy, updateStrategy } from '../../swr-config';
import type {
  BetStrategy,
  BetStrategyCreate,
  BetTypeOption,
  ConfidenceLevelOption,
  FixtureTypeOption,
  DerbyIntensityOption,
  LookbackOption,
  VenueOption,
  TeamScopeOption,
  OperatorOption,
  TeamStatFilter,
  TeamStatsFilters,
} from '../../types';

interface StrategyCreatorModalProps {
  strategy: BetStrategy | null;
  onClose: () => void;
  onSave: () => void;
}

const BET_TYPES: { value: BetTypeOption; label: string; group: 'goals' | 'cards' }[] = [
  { value: 'btts', label: 'BTTS', group: 'goals' },
  { value: 'o2_5', label: 'Over 2.5 Goals', group: 'goals' },
  { value: 'btts_o2_5', label: 'BTTS + Over 2.5', group: 'goals' },
  { value: 'btts_winner_home', label: 'Home Win + BTTS', group: 'goals' },
  { value: 'btts_winner_away', label: 'Away Win + BTTS', group: 'goals' },
  { value: 'winner_o2_5_home', label: 'Home Win + Over 2.5', group: 'goals' },
  { value: 'winner_o2_5_away', label: 'Away Win + Over 2.5', group: 'goals' },
  { value: 'cards_total_ou', label: 'Total Cards O/U', group: 'cards' },
  { value: 'cards_team_home', label: 'Home Team Cards O1.5', group: 'cards' },
  { value: 'cards_team_away', label: 'Away Team Cards O1.5', group: 'cards' },
  { value: 'cards_first_timing', label: 'First Card <15 min', group: 'cards' },
  { value: 'cards_red', label: 'Red Card', group: 'cards' },
];

const CONFIDENCE_LEVELS: { value: ConfidenceLevelOption; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'low', label: 'Low' },
  { value: 'speculative', label: 'Speculative' },
];

const DAYS_OPTIONS = [
  { value: 1, label: '24 hours' },
  { value: 2, label: '48 hours' },
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
];

const CARDS_LINES = [2.5, 3.5, 4.5, 5.5, 6.5];

const FIXTURE_TYPES: { value: FixtureTypeOption; label: string }[] = [
  { value: 'derby', label: 'Derbies' },
  { value: 'relegation_battle', label: 'Relegation Battles' },
  { value: 'title_race', label: 'Title Races' },
];

const DERBY_INTENSITIES: { value: DerbyIntensityOption; label: string }[] = [
  { value: 'fierce', label: 'Fierce' },
  { value: 'regional', label: 'Regional' },
  { value: 'historical', label: 'Historical' },
];

// Team Stats Filter Options
const LOOKBACK_OPTIONS: { value: LookbackOption; label: string }[] = [
  { value: 'last3', label: 'Last 3' },
  { value: 'last5', label: 'Last 5' },
  { value: 'last10', label: 'Last 10' },
  { value: 'season', label: 'Season' },
];

const VENUE_OPTIONS: { value: VenueOption; label: string }[] = [
  { value: 'all', label: 'All Games' },
  { value: 'home', label: 'Home Only' },
  { value: 'away', label: 'Away Only' },
];

const TEAM_SCOPE_OPTIONS: { value: TeamScopeOption; label: string }[] = [
  { value: 'either', label: 'Either Team' },
  { value: 'both', label: 'Both Teams' },
  { value: 'home', label: 'Home Team' },
  { value: 'away', label: 'Away Team' },
];

const OPERATOR_OPTIONS: { value: OperatorOption; label: string }[] = [
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
];

const STAT_OPTIONS: { value: string; label: string; group: string }[] = [
  // Goals & Scoring
  { value: 'avg_goals_for', label: 'Avg Goals Scored', group: 'Goals & Scoring' },
  { value: 'avg_goals_against', label: 'Avg Goals Conceded', group: 'Goals & Scoring' },
  { value: 'scored_rate', label: 'Games Scoring %', group: 'Goals & Scoring' },
  { value: 'conceded_rate', label: 'Games Conceding %', group: 'Goals & Scoring' },
  { value: 'btts_rate', label: 'BTTS Rate %', group: 'Goals & Scoring' },
  { value: 'clean_sheet_rate', label: 'Clean Sheet %', group: 'Goals & Scoring' },
  // Shots & Conversion
  { value: 'avg_shots', label: 'Avg Shots', group: 'Shots & Conversion' },
  { value: 'avg_shots_against', label: 'Avg Shots Against', group: 'Shots & Conversion' },
  { value: 'avg_shots_on_target', label: 'Avg SOT', group: 'Shots & Conversion' },
  { value: 'avg_sot_against', label: 'Avg SOT Against', group: 'Shots & Conversion' },
  { value: 'shot_conversion_rate', label: 'Shot Conversion %', group: 'Shots & Conversion' },
  // Expected Goals
  { value: 'avg_xg', label: 'Avg xG', group: 'Expected Goals' },
  { value: 'avg_xg_against', label: 'Avg xG Against', group: 'Expected Goals' },
  // Corners
  { value: 'avg_corners', label: 'Avg Corners', group: 'Corners' },
  { value: 'avg_corners_against', label: 'Avg Corners Against', group: 'Corners' },
  // Possession & Passing
  { value: 'avg_possession', label: 'Avg Possession %', group: 'Possession & Passing' },
  { value: 'pass_accuracy_pct', label: 'Pass Accuracy %', group: 'Possession & Passing' },
  // Discipline
  { value: 'avg_cards_y', label: 'Avg Yellow Cards', group: 'Discipline' },
  { value: 'avg_cards_y_against', label: 'Avg Yellows Against', group: 'Discipline' },
  { value: 'avg_fouls', label: 'Avg Fouls Committed', group: 'Discipline' },
  { value: 'avg_fouls_against', label: 'Avg Fouls Won', group: 'Discipline' },
  // Defensive
  { value: 'avg_tackles', label: 'Avg Tackles', group: 'Defensive' },
  { value: 'avg_interceptions', label: 'Avg Interceptions', group: 'Defensive' },
  { value: 'avg_clearances', label: 'Avg Clearances', group: 'Defensive' },
  { value: 'duel_win_pct', label: 'Duel Win %', group: 'Defensive' },
];

type PresetConfig = {
  name: string;
  filters: TeamStatFilter[];
};

const TEAM_STATS_PRESETS: PresetConfig[] = [
  {
    name: 'High Scoring',
    filters: [
      { stat_key: 'avg_goals_for', operator: 'gte', value: 1.5, team_scope: 'either' },
      { stat_key: 'btts_rate', operator: 'gte', value: 0.5, team_scope: 'both' },
    ],
  },
  {
    name: 'Defensive',
    filters: [
      { stat_key: 'clean_sheet_rate', operator: 'gte', value: 0.3, team_scope: 'either' },
      { stat_key: 'avg_goals_against', operator: 'lte', value: 1.0, team_scope: 'either' },
    ],
  },
  {
    name: 'Corner Heavy',
    filters: [
      { stat_key: 'avg_corners', operator: 'gte', value: 5.0, team_scope: 'either' },
    ],
  },
  {
    name: 'Card Heavy',
    filters: [
      { stat_key: 'avg_cards_y', operator: 'gte', value: 2.0, team_scope: 'either' },
    ],
  },
  {
    name: 'xG Value',
    filters: [
      { stat_key: 'avg_xg', operator: 'gte', value: 1.5, team_scope: 'either' },
      { stat_key: 'avg_xg_against', operator: 'lte', value: 1.5, team_scope: 'either' },
    ],
  },
];

export function StrategyCreatorModal({
  strategy,
  onClose,
  onSave,
}: StrategyCreatorModalProps) {
  const isEditing = strategy !== null;

  const [name, setName] = useState(strategy?.name || '');
  const [description, setDescription] = useState(strategy?.description || '');
  const [daysAhead, setDaysAhead] = useState(strategy?.days_ahead || 7);
  const [selectedBetTypes, setSelectedBetTypes] = useState<BetTypeOption[]>(
    strategy?.bet_types || []
  );
  const [minProbability, setMinProbability] = useState<number | null>(
    strategy?.min_probability != null ? strategy.min_probability * 100 : null
  );
  const [maxProbability, setMaxProbability] = useState<number | null>(
    strategy?.max_probability != null ? strategy.max_probability * 100 : null
  );
  const [confidenceLevels, setConfidenceLevels] = useState<ConfidenceLevelOption[]>(
    strategy?.confidence_levels || []
  );
  const [cardsLine, setCardsLine] = useState<number | null>(strategy?.cards_line || 3.5);
  const [cardsDirection, setCardsDirection] = useState<'over' | 'under'>(
    strategy?.cards_direction || 'over'
  );
  const [alertsEnabled, setAlertsEnabled] = useState(strategy?.alerts_enabled || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'team_stats'>('basic');

  // Fixture Type Filters
  const [fixtureTypes, setFixtureTypes] = useState<FixtureTypeOption[]>(
    strategy?.fixture_types || []
  );
  const [excludeFixtureTypes, setExcludeFixtureTypes] = useState<FixtureTypeOption[]>(
    strategy?.exclude_fixture_types || []
  );
  const [derbyIntensity, setDerbyIntensity] = useState<DerbyIntensityOption[]>(
    strategy?.derby_intensity || []
  );

  // Referee Filters
  const [refereeStrictnessMin, setRefereeStrictnessMin] = useState<number | null>(
    strategy?.referee_strictness_min ?? null
  );
  const [refereeStrictnessMax, setRefereeStrictnessMax] = useState<number | null>(
    strategy?.referee_strictness_max ?? null
  );
  const [refereeCardsMin, setRefereeCardsMin] = useState<number | null>(
    strategy?.referee_cards_per_90_min ?? null
  );
  const [refereeCardsMax, setRefereeCardsMax] = useState<number | null>(
    strategy?.referee_cards_per_90_max ?? null
  );

  // Team Form Filters
  const [minFormPoints, setMinFormPoints] = useState<number | null>(
    strategy?.min_form_points ?? null
  );
  const [homeTeamFormMin, setHomeTeamFormMin] = useState<number | null>(
    strategy?.home_team_form_min ?? null
  );
  const [awayTeamFormMin, setAwayTeamFormMin] = useState<number | null>(
    strategy?.away_team_form_min ?? null
  );

  // Team Stats Filters
  const [minAvgGoals, setMinAvgGoals] = useState<number | null>(
    strategy?.min_avg_goals_scored ?? null
  );
  const [minBttsRate, setMinBttsRate] = useState<number | null>(
    strategy?.min_btts_rate != null ? strategy.min_btts_rate * 100 : null
  );
  const [minCleanSheetRate, setMinCleanSheetRate] = useState<number | null>(
    strategy?.min_clean_sheet_rate != null ? strategy.min_clean_sheet_rate * 100 : null
  );

  // Expected Value Filters
  const [minExpectedGoals, setMinExpectedGoals] = useState<number | null>(
    strategy?.min_expected_goals ?? null
  );
  const [minExpectedCards, setMinExpectedCards] = useState<number | null>(
    strategy?.min_expected_cards ?? null
  );

  // Team Stats Filters (JSONB-based)
  const [teamStatsLookback, setTeamStatsLookback] = useState<LookbackOption>(
    strategy?.team_stats_filters?.lookback || 'last5'
  );
  const [teamStatsVenue, setTeamStatsVenue] = useState<VenueOption>(
    strategy?.team_stats_filters?.venue || 'all'
  );
  const [teamStatsFilters, setTeamStatsFilters] = useState<TeamStatFilter[]>(
    strategy?.team_stats_filters?.filters || []
  );

  const hasCardsTotal = selectedBetTypes.includes('cards_total_ou');
  const hasAdvancedFilters = fixtureTypes.length > 0 || excludeFixtureTypes.length > 0 ||
    refereeStrictnessMin !== null || refereeStrictnessMax !== null ||
    refereeCardsMin !== null || refereeCardsMax !== null ||
    minFormPoints !== null || homeTeamFormMin !== null || awayTeamFormMin !== null ||
    minAvgGoals !== null || minBttsRate !== null || minCleanSheetRate !== null ||
    minExpectedGoals !== null || minExpectedCards !== null;

  const hasTeamStatsFilters = teamStatsFilters.length > 0;

  const toggleBetType = useCallback((type: BetTypeOption) => {
    setSelectedBetTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const toggleConfidence = useCallback((level: ConfidenceLevelOption) => {
    setConfidenceLevels(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  }, []);

  const toggleFixtureType = useCallback((type: FixtureTypeOption) => {
    setFixtureTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const toggleExcludeFixtureType = useCallback((type: FixtureTypeOption) => {
    setExcludeFixtureTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const toggleDerbyIntensity = useCallback((intensity: DerbyIntensityOption) => {
    setDerbyIntensity(prev =>
      prev.includes(intensity)
        ? prev.filter(i => i !== intensity)
        : [...prev, intensity]
    );
  }, []);

  // Team Stats Filter callbacks
  const addStatFilter = useCallback(() => {
    setTeamStatsFilters(prev => [
      ...prev,
      { stat_key: 'avg_goals_for', operator: 'gte', value: 1.0, team_scope: 'either' },
    ]);
  }, []);

  const removeStatFilter = useCallback((index: number) => {
    setTeamStatsFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateStatFilter = useCallback((index: number, field: keyof TeamStatFilter, value: any) => {
    setTeamStatsFilters(prev => prev.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    ));
  }, []);

  const applyPreset = useCallback((preset: PresetConfig) => {
    setTeamStatsFilters(preset.filters);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Strategy name is required');
      return;
    }
    if (selectedBetTypes.length === 0) {
      setError('At least one bet type must be selected');
      return;
    }
    if (hasCardsTotal && cardsLine === null) {
      setError('Cards line is required for Total Cards O/U');
      return;
    }

    const payload: BetStrategyCreate = {
      name: name.trim(),
      description: description.trim() || null,
      days_ahead: daysAhead,
      bet_types: selectedBetTypes,
      min_probability: minProbability !== null ? minProbability / 100 : null,
      max_probability: maxProbability !== null ? maxProbability / 100 : null,
      confidence_levels: confidenceLevels.length > 0 ? confidenceLevels : [],
      cards_line: hasCardsTotal ? cardsLine! : null,
      cards_direction: hasCardsTotal ? cardsDirection : null,
      // Advanced Filters - use null to clear, not undefined
      fixture_types: fixtureTypes.length > 0 ? fixtureTypes : null,
      exclude_fixture_types: excludeFixtureTypes.length > 0 ? excludeFixtureTypes : null,
      derby_intensity: derbyIntensity.length > 0 ? derbyIntensity : null,
      referee_strictness_min: refereeStrictnessMin,
      referee_strictness_max: refereeStrictnessMax,
      referee_cards_per_90_min: refereeCardsMin,
      referee_cards_per_90_max: refereeCardsMax,
      min_form_points: minFormPoints,
      home_team_form_min: homeTeamFormMin,
      away_team_form_min: awayTeamFormMin,
      min_avg_goals_scored: minAvgGoals,
      min_btts_rate: minBttsRate !== null ? minBttsRate / 100 : null,
      min_clean_sheet_rate: minCleanSheetRate !== null ? minCleanSheetRate / 100 : null,
      min_expected_goals: minExpectedGoals,
      min_expected_cards: minExpectedCards,
      // Team Stats Filters (JSONB)
      team_stats_filters: teamStatsFilters.length > 0 ? {
        lookback: teamStatsLookback,
        venue: teamStatsVenue,
        filters: teamStatsFilters,
      } : null,
      alerts_enabled: alertsEnabled,
    };

    setIsSubmitting(true);
    try {
      if (isEditing) {
        await updateStrategy(strategy!.id, payload);
      } else {
        await createStrategy(payload);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save strategy');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name, description, daysAhead, selectedBetTypes,
    minProbability, maxProbability, confidenceLevels,
    cardsLine, cardsDirection, alertsEnabled,
    fixtureTypes, excludeFixtureTypes, derbyIntensity,
    refereeStrictnessMin, refereeStrictnessMax, refereeCardsMin, refereeCardsMax,
    minFormPoints, homeTeamFormMin, awayTeamFormMin,
    minAvgGoals, minBttsRate, minCleanSheetRate,
    minExpectedGoals, minExpectedCards,
    teamStatsLookback, teamStatsVenue, teamStatsFilters,
    hasCardsTotal, isEditing, strategy, onSave
  ]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content strategy-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Strategy' : 'Create Strategy'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="strategy-form">
          {error && <div className="strategy-form-error">{error}</div>}

          {/* Tabs */}
          <div className="strategy-tabs">
            <button
              type="button"
              className={`strategy-tab ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic
            </button>
            <button
              type="button"
              className={`strategy-tab ${activeTab === 'advanced' ? 'active' : ''}`}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced Filters
              {hasAdvancedFilters && <span className="badge-dot" />}
            </button>
            <button
              type="button"
              className={`strategy-tab ${activeTab === 'team_stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('team_stats')}
            >
              Team Stats
              {hasTeamStatsFilters && <span className="badge-dot" />}
            </button>
          </div>

          {activeTab === 'basic' && (
            <>
          {/* Name */}
          <div className="form-group">
            <label htmlFor="name">Strategy Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., High BTTS in Top 5 Leagues"
              maxLength={100}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description of your strategy..."
              rows={2}
            />
          </div>

          {/* Time Range */}
          <div className="form-group">
            <label>Time Range</label>
            <div className="form-chips">
              {DAYS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`form-chip ${daysAhead === opt.value ? 'active' : ''}`}
                  onClick={() => setDaysAhead(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bet Types */}
          <div className="form-group">
            <label>Bet Types *</label>
            <div className="form-group-section">
              <span className="form-group-sublabel">Goals Markets</span>
              <div className="form-chips">
                {BET_TYPES.filter(t => t.group === 'goals').map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`form-chip ${selectedBetTypes.includes(type.value) ? 'active' : ''}`}
                    onClick={() => toggleBetType(type.value)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group-section">
              <span className="form-group-sublabel">Cards Markets</span>
              <div className="form-chips">
                {BET_TYPES.filter(t => t.group === 'cards').map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`form-chip ${selectedBetTypes.includes(type.value) ? 'active' : ''}`}
                    onClick={() => toggleBetType(type.value)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Cards Line (only if cards_total_ou selected) */}
          {hasCardsTotal && (
            <div className="form-group">
              <label>Cards Line</label>
              <div className="form-row">
                <select
                  value={cardsDirection}
                  onChange={e => setCardsDirection(e.target.value as 'over' | 'under')}
                  className="form-select"
                >
                  <option value="over">Over</option>
                  <option value="under">Under</option>
                </select>
                <select
                  value={cardsLine || ''}
                  onChange={e => setCardsLine(parseFloat(e.target.value))}
                  className="form-select"
                >
                  {CARDS_LINES.map(line => (
                    <option key={line} value={line}>{line}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Probability Range */}
          <div className="form-group">
            <label>Probability Range</label>
            <div className="form-row">
              <div className="form-input-group">
                <label className="form-input-label">Min %</label>
                <input
                  type="number"
                  value={minProbability ?? ''}
                  onChange={e => setMinProbability(e.target.value ? parseFloat(e.target.value) : null)}
                  min={0}
                  max={100}
                  placeholder="0"
                />
              </div>
              <span className="form-row-separator">to</span>
              <div className="form-input-group">
                <label className="form-input-label">Max %</label>
                <input
                  type="number"
                  value={maxProbability ?? ''}
                  onChange={e => setMaxProbability(e.target.value ? parseFloat(e.target.value) : null)}
                  min={0}
                  max={100}
                  placeholder="100"
                />
              </div>
            </div>
          </div>

          {/* Confidence Levels */}
          <div className="form-group">
            <label>Confidence Levels (leave empty for all)</label>
            <div className="form-chips">
              {CONFIDENCE_LEVELS.map(level => (
                <button
                  key={level.value}
                  type="button"
                  className={`form-chip ${confidenceLevels.includes(level.value) ? 'active' : ''}`}
                  onClick={() => toggleConfidence(level.value)}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>
            </>
          )}

          {activeTab === 'advanced' && (
            <div className="advanced-filters-content">
              {/* Fixture Type Section */}
              <div className="form-group">
                <label>Include Fixture Types</label>
                <div className="form-chips">
                  {FIXTURE_TYPES.map(ft => (
                    <button
                      key={ft.value}
                      type="button"
                      className={`form-chip ${fixtureTypes.includes(ft.value) ? 'active' : ''}`}
                      onClick={() => toggleFixtureType(ft.value)}
                    >
                      {ft.label}
                    </button>
                  ))}
                </div>
                <span className="form-help">Only show matches of these types (empty = all)</span>

                {/* Derby Intensity (only shown when derby selected) */}
                {fixtureTypes.includes('derby') && (
                  <div className="form-group-section" style={{ marginTop: 12 }}>
                    <span className="form-group-sublabel">Derby Intensity</span>
                    <div className="form-chips">
                      {DERBY_INTENSITIES.map(di => (
                        <button
                          key={di.value}
                          type="button"
                          className={`form-chip ${derbyIntensity.includes(di.value) ? 'active' : ''}`}
                          onClick={() => toggleDerbyIntensity(di.value)}
                        >
                          {di.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Exclude Fixture Types</label>
                <div className="form-chips">
                  {FIXTURE_TYPES.map(ft => (
                    <button
                      key={ft.value}
                      type="button"
                      className={`form-chip ${excludeFixtureTypes.includes(ft.value) ? 'active' : ''}`}
                      onClick={() => toggleExcludeFixtureType(ft.value)}
                    >
                      {ft.label}
                    </button>
                  ))}
                </div>
                <span className="form-help">Hide matches of these types</span>
              </div>

              {/* Referee Section */}
              <div className="form-group">
                <label>Referee Strictness (Percentile)</label>
                <div className="form-row">
                  <div className="form-input-group">
                    <label className="form-input-label">Min %</label>
                    <input
                      type="number"
                      value={refereeStrictnessMin ?? ''}
                      onChange={e => setRefereeStrictnessMin(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      max={100}
                      placeholder="0"
                    />
                  </div>
                  <span className="form-row-separator">to</span>
                  <div className="form-input-group">
                    <label className="form-input-label">Max %</label>
                    <input
                      type="number"
                      value={refereeStrictnessMax ?? ''}
                      onChange={e => setRefereeStrictnessMax(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      max={100}
                      placeholder="100"
                    />
                  </div>
                </div>
                <span className="form-help">Higher percentile = stricter referee</span>
              </div>

              <div className="form-group">
                <label>Referee Cards Per Match</label>
                <div className="form-row">
                  <div className="form-input-group">
                    <label className="form-input-label">Min</label>
                    <input
                      type="number"
                      value={refereeCardsMin ?? ''}
                      onChange={e => setRefereeCardsMin(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      step={0.1}
                      placeholder="0"
                    />
                  </div>
                  <span className="form-row-separator">to</span>
                  <div className="form-input-group">
                    <label className="form-input-label">Max</label>
                    <input
                      type="number"
                      value={refereeCardsMax ?? ''}
                      onChange={e => setRefereeCardsMax(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      step={0.1}
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>

              {/* Team Form Section */}
              <div className="form-group">
                <label>Team Form (Last 5 Matches)</label>
                <div className="form-row">
                  <div className="form-input-group">
                    <label className="form-input-label">Either Team Min Points</label>
                    <input
                      type="number"
                      value={minFormPoints ?? ''}
                      onChange={e => setMinFormPoints(e.target.value ? parseInt(e.target.value) : null)}
                      min={0}
                      max={15}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: 8 }}>
                  <div className="form-input-group">
                    <label className="form-input-label">Home Team Min</label>
                    <input
                      type="number"
                      value={homeTeamFormMin ?? ''}
                      onChange={e => setHomeTeamFormMin(e.target.value ? parseInt(e.target.value) : null)}
                      min={0}
                      max={15}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-input-group">
                    <label className="form-input-label">Away Team Min</label>
                    <input
                      type="number"
                      value={awayTeamFormMin ?? ''}
                      onChange={e => setAwayTeamFormMin(e.target.value ? parseInt(e.target.value) : null)}
                      min={0}
                      max={15}
                      placeholder="0"
                    />
                  </div>
                </div>
                <span className="form-help">0-15 points (W=3, D=1, L=0)</span>
              </div>

              {/* Team Stats Section */}
              <div className="form-group">
                <label>Team Stats Requirements</label>
                <div className="form-row">
                  <div className="form-input-group">
                    <label className="form-input-label">Min Avg Goals Scored</label>
                    <input
                      type="number"
                      value={minAvgGoals ?? ''}
                      onChange={e => setMinAvgGoals(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      step={0.1}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: 8 }}>
                  <div className="form-input-group">
                    <label className="form-input-label">Min BTTS Rate %</label>
                    <input
                      type="number"
                      value={minBttsRate ?? ''}
                      onChange={e => setMinBttsRate(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      max={100}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-input-group">
                    <label className="form-input-label">Min Clean Sheet %</label>
                    <input
                      type="number"
                      value={minCleanSheetRate ?? ''}
                      onChange={e => setMinCleanSheetRate(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      max={100}
                      placeholder="0"
                    />
                  </div>
                </div>
                <span className="form-help">Either team must meet threshold</span>
              </div>

              {/* Expected Values Section */}
              <div className="form-group">
                <label>Minimum Expected Values</label>
                <div className="form-row">
                  <div className="form-input-group">
                    <label className="form-input-label">Expected Goals</label>
                    <input
                      type="number"
                      value={minExpectedGoals ?? ''}
                      onChange={e => setMinExpectedGoals(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      step={0.1}
                      placeholder="0"
                    />
                  </div>
                  <div className="form-input-group">
                    <label className="form-input-label">Expected Cards</label>
                    <input
                      type="number"
                      value={minExpectedCards ?? ''}
                      onChange={e => setMinExpectedCards(e.target.value ? parseFloat(e.target.value) : null)}
                      min={0}
                      step={0.1}
                      placeholder="0"
                    />
                  </div>
                </div>
                <span className="form-help">From prediction models</span>
              </div>
            </div>
          )}

          {activeTab === 'team_stats' && (
            <div className="team-stats-content">
              {/* Lookback Period */}
              <div className="form-group">
                <label>Lookback Period</label>
                <div className="form-chips">
                  {LOOKBACK_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`form-chip ${teamStatsLookback === opt.value ? 'active' : ''}`}
                      onClick={() => setTeamStatsLookback(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Venue */}
              <div className="form-group">
                <label>Venue</label>
                <div className="form-chips">
                  {VENUE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`form-chip ${teamStatsVenue === opt.value ? 'active' : ''}`}
                      onClick={() => setTeamStatsVenue(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Presets */}
              <div className="form-group">
                <label>Presets</label>
                <div className="preset-buttons">
                  {TEAM_STATS_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      type="button"
                      className="btn-preset"
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stat Filters */}
              <div className="form-group">
                <div className="stat-filter-header">
                  <label>Stat Filters ({teamStatsFilters.length} active)</label>
                  <button
                    type="button"
                    className="btn-add-filter"
                    onClick={addStatFilter}
                  >
                    + Add Filter
                  </button>
                </div>

                <div className="stat-filter-builder">
                  {teamStatsFilters.length === 0 ? (
                    <div className="stat-filter-empty">
                      No filters added. Click "Add Filter" or choose a preset to get started.
                    </div>
                  ) : (
                    teamStatsFilters.map((filter, index) => (
                      <div key={index} className="stat-filter-row">
                        <select
                          className="stat-select"
                          value={filter.stat_key}
                          onChange={e => updateStatFilter(index, 'stat_key', e.target.value)}
                        >
                          {Object.entries(
                            STAT_OPTIONS.reduce((acc, opt) => {
                              if (!acc[opt.group]) acc[opt.group] = [];
                              acc[opt.group].push(opt);
                              return acc;
                            }, {} as Record<string, typeof STAT_OPTIONS>)
                          ).map(([group, opts]) => (
                            <optgroup key={group} label={group}>
                              {opts.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>

                        <select
                          className="operator-select"
                          value={filter.operator}
                          onChange={e => updateStatFilter(index, 'operator', e.target.value as OperatorOption)}
                        >
                          {OPERATOR_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <input
                          type="number"
                          className="value-input"
                          value={filter.value}
                          onChange={e => updateStatFilter(index, 'value', parseFloat(e.target.value) || 0)}
                          step={0.1}
                          min={0}
                        />

                        <select
                          className="team-scope-select"
                          value={filter.team_scope}
                          onChange={e => updateStatFilter(index, 'team_scope', e.target.value as TeamScopeOption)}
                        >
                          {TEAM_SCOPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          className="btn-remove-filter"
                          onClick={() => removeStatFilter(index)}
                          title="Remove filter"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <span className="form-help">Fixtures excluded if team data unavailable (strict mode)</span>
              </div>
            </div>
          )}

          {/* Alerts Toggle (always visible) */}
          <div className="form-group form-group-row">
            <label htmlFor="alerts">Enable Alerts</label>
            <label className="toggle-switch">
              <input
                id="alerts"
                type="checkbox"
                checked={alertsEnabled}
                onChange={e => setAlertsEnabled(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
            <span className="form-help">Get notified when new matches are found</span>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Strategy' : 'Create Strategy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StrategyCreatorModal;
